import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Bell,
  RefreshCw,
  Zap,
  Activity,
  History,
} from 'lucide-react';
import { api } from '@shared/services/api';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneIconBgClasses } from '@shared/utils/statusTone';

interface MetricsAlertsConfig {
  cpu_threshold: number;
  memory_threshold: number;
  disk_threshold: number;
  enabled: boolean;
}

interface AlertHistory {
  id: string;
  agent_name?: string;
  alert_type: string;
  threshold_value: number;
  actual_value: number;
  notification_sent: boolean;
  created_at: string;
  resolved_at?: string;
}

export const MetricsAlerts: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [alerts, setAlerts] = useState<MetricsAlertsConfig>({
    cpu_threshold: 80,
    memory_threshold: 90,
    disk_threshold: 95,
    enabled: true,
  });
  const [history, setHistory] = useState<AlertHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [configLoadError, setConfigLoadError] = useState(false);
  const [historyLoadError, setHistoryLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const initialFetchDone = useRef(false);

  const formatAlertType = (value: string) => {
    const keyByType: Record<string, string> = {
      CPU: 'metricsAlerts.types.cpuCritical',
      CPU_THRESHOLD: 'metricsAlerts.types.cpuCritical',
      CPU_CRITICAL: 'metricsAlerts.types.cpuCritical',
      MEMORY: 'metricsAlerts.types.memoryLimit',
      MEMORY_THRESHOLD: 'metricsAlerts.types.memoryLimit',
      MEMORY_LIMIT: 'metricsAlerts.types.memoryLimit',
      DISK: 'metricsAlerts.types.diskCapacity',
      DISK_THRESHOLD: 'metricsAlerts.types.diskCapacity',
      DISK_CAPACITY: 'metricsAlerts.types.diskCapacity',
    };
    const key = keyByType[value.toUpperCase()];
    return key ? t(key) : value.replace(/_/g, ' ');
  };

  const formatDate = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? t('metricsAlerts.notAvailable')
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
  };

  const formatTime = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? t('metricsAlerts.notAvailable')
      : new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(date);
  };

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;
    fetchAlerts();
    fetchHistory();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    setConfigLoadError(false);
    try {
      const res = await api.get('/metrics/alerts');
      setAlerts(res.data);
    } catch {
      setConfigLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryLoadError(false);
    try {
      const res = await api.get('/metrics/alerts/history?limit=20');
      setHistory(res.data.history || []);
    } catch {
      setHistoryLoadError(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.put('/metrics/alerts', alerts);
      setMessage({ type: 'success', text: t('metricsAlerts.messages.saved') });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: t('metricsAlerts.errors.save') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <Button
              variant="ghost"
              size="md"
              onClick={() => navigate('/dashboard')}
              aria-label={t('metricsAlerts.backToDashboard')}
              className="shrink-0 p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
            >
              <ArrowLeft
                size={20}
                aria-hidden="true"
                className="text-text-subtle-light dark:text-text-subtle-dark"
              />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
                {t('metricsAlerts.title')}
              </h1>
              <p className="text-sm sm:text-base text-text-muted-light dark:text-text-muted-dark mt-1">
                {t('metricsAlerts.description')}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="md"
            onClick={fetchHistory}
            disabled={historyLoading}
            className="shrink-0 whitespace-nowrap"
          >
            <RefreshCw size={16} aria-hidden="true" className="mr-2" /> {t('metricsAlerts.syncLog')}
          </Button>
        </div>

        {message && (
          <div
            role="status"
            aria-live="polite"
            className={`p-4 rounded-xl border font-bold flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.type === 'success' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
            {message.text}
          </div>
        )}

        {configLoadError && (
          <div
            role="alert"
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-4 text-sm font-medium text-state-danger-light dark:text-state-danger-dark"
          >
            <span>{t('metricsAlerts.errors.loadConfig')}</span>
            <Button variant="outline" size="md" onClick={fetchAlerts} disabled={loading}>
              {t('metricsAlerts.retry')}
            </Button>
          </div>
        )}
        {historyLoadError && (
          <div
            role="alert"
            className="rounded-xl border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-4 text-sm font-medium text-state-danger-light dark:text-state-danger-dark"
          >
            {t('metricsAlerts.errors.loadHistory')}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 sm:gap-6">
          <div className="xl:col-span-1 space-y-5 sm:space-y-6">
            <Card className="p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 mb-5">
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('metricsAlerts.thresholds')}
                </h2>
              </div>

              {loading && (
                <div
                  className="py-8 text-center text-sm text-text-muted-light dark:text-text-muted-dark"
                  role="status"
                >
                  {t('metricsAlerts.loadingConfig')}
                </div>
              )}
              {configLoadError && (
                <div className="rounded-xl border border-border-strong-light dark:border-border-strong-dark bg-surface-2-light dark:bg-surface-2-dark p-4 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('metricsAlerts.thresholdsUnavailable')}
                </div>
              )}
              {!loading && !configLoadError && (
                <>
                  <label className="mb-5 min-h-11 inline-flex items-center gap-2 cursor-pointer text-text-muted-light dark:text-text-muted-dark">
                    <input
                      type="checkbox"
                      id="metrics-alerts-enabled"
                      checked={alerts.enabled}
                      onChange={(e) => setAlerts({ ...alerts, enabled: e.target.checked })}
                      className="rounded border-border-strong-light dark:border-border-strong-dark text-brand-600 dark:text-brand-400"
                    />
                    <span className="text-xs font-bold uppercase">{t('metricsAlerts.active')}</span>
                  </label>
                  <div className="space-y-6">
                    <ThresholdInput
                      id="metrics-alerts-cpu"
                      label={t('metricsAlerts.cpuCritical')}
                      value={alerts.cpu_threshold}
                      onChange={(v: number) => setAlerts({ ...alerts, cpu_threshold: v })}
                      color="text-brand-600 dark:text-brand-400"
                      icon={<Zap size={14} />}
                      disabled={false}
                    />
                    <ThresholdInput
                      id="metrics-alerts-memory"
                      label={t('metricsAlerts.memoryLimit')}
                      value={alerts.memory_threshold}
                      onChange={(v: number) => setAlerts({ ...alerts, memory_threshold: v })}
                      color="text-state-ok-light dark:text-state-ok-dark"
                      icon={<Activity size={14} />}
                      disabled={false}
                    />
                    <ThresholdInput
                      id="metrics-alerts-disk"
                      label={t('metricsAlerts.diskCapacity')}
                      value={alerts.disk_threshold}
                      onChange={(v: number) => setAlerts({ ...alerts, disk_threshold: v })}
                      color="text-state-warn-light dark:text-state-warn-dark"
                      icon={<Shield size={14} />}
                      disabled={false}
                    />

                    <div className="pt-6 border-t border-surface-border-light dark:border-surface-border-dark flex justify-end">
                      <Button
                        onClick={handleSave}
                        isLoading={saving}
                        disabled={saving}
                        className="w-full"
                      >
                        {t('metricsAlerts.updatePolicies')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>

            <Card className="p-5 sm:p-6 bg-accent-soft-light dark:bg-surface-2-dark border border-accent-text-light/20 dark:border-surface-border-dark relative overflow-hidden">
              <Bell
                size={120}
                aria-hidden="true"
                className="absolute -bottom-8 -right-8 opacity-10 rotate-12 text-accent-text-light dark:text-accent-text-dark"
              />
              <h3 className="text-sm font-bold uppercase tracking-widest text-accent-text-light dark:text-accent-text-dark mb-4 relative z-10">
                {t('metricsAlerts.smartAlerts')}
              </h3>
              <p className="text-sm leading-relaxed text-accent-text-light dark:text-text-muted-dark relative z-10">
                {t('metricsAlerts.smartAlertsDescription')}
              </p>
            </Card>
          </div>

          <div className="xl:col-span-2">
            <Card className="overflow-hidden h-full">
              <div className="p-5 sm:p-6 bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark">
                <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark flex items-center gap-2">
                  <History
                    size={16}
                    aria-hidden="true"
                    className="text-brand-600 dark:text-brand-400"
                  />
                  {t('metricsAlerts.recentIncidentLog')}
                </h3>
              </div>
              <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {history.map((item) => {
                  // An unresolved threshold breach is an active alert; a resolved one is a
                  // recovered incident. The label is UI text; the tone is domain vocabulary.
                  const alertTone = getStatusTone(item.resolved_at ? 'RECOVERED' : 'ALERTING');
                  return (
                    <div
                      key={item.id}
                      className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl ${toneIconBgClasses[alertTone]}`}>
                          <AlertTriangle size={20} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                              {formatAlertType(item.alert_type)}
                            </h4>
                            <Badge variant={toneToBadgeVariant[alertTone]} className="text-[10px]">
                              {item.resolved_at
                                ? t('metricsAlerts.resolved')
                                : t('metricsAlerts.active')}
                            </Badge>
                          </div>
                          <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                            {t('metricsAlerts.actualValue')}{' '}
                            <span className="font-bold text-text-primary-light dark:text-text-primary-dark">
                              {new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
                                item.actual_value
                              )}
                              %
                            </span>{' '}
                            {t('metricsAlerts.exceededThreshold', {
                              threshold: new Intl.NumberFormat(locale, {
                                maximumFractionDigits: 1,
                              }).format(item.threshold_value),
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="sm:text-right">
                        <div className="text-xs font-semibold text-text-primary-light dark:text-text-primary-dark">
                          {formatDate(item.created_at)}
                        </div>
                        <div className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                          {formatTime(item.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {historyLoading && (
                  <div
                    className="p-10 text-center text-text-muted-light dark:text-text-muted-dark"
                    role="status"
                  >
                    {t('metricsAlerts.loadingHistory')}
                  </div>
                )}
                {!historyLoading && !historyLoadError && history.length === 0 && (
                  <div className="p-10 sm:p-16 text-center text-text-muted-light dark:text-text-muted-dark font-medium">
                    {t('metricsAlerts.emptyHistory')}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

interface ThresholdInputProps {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  color: string;
  icon: React.ReactNode;
  disabled: boolean;
}

function ThresholdInput({
  id,
  label,
  value,
  onChange,
  color,
  icon,
  disabled,
}: ThresholdInputProps) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label
          htmlFor={id}
          className={`text-[10px] font-black uppercase tracking-widest ${color} flex items-center gap-1.5`}
        >
          {icon} {label}
        </label>
        <span className="text-sm font-bold text-text-primary-light dark:text-text-primary-dark">
          {value}%
        </span>
      </div>
      <input
        id={id}
        type="range"
        min="0"
        max="100"
        value={value}
        disabled={disabled}
        aria-valuetext={`${value}%`}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="min-h-11 w-full bg-surface-2-light dark:bg-surface-2-dark rounded-lg appearance-none cursor-pointer accent-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  );
}

export default MetricsAlerts;
